/**
 * The config screen's view of the descriptor → brand mapping.
 *
 * Kept out of the component for the usual reason: the interesting part of the
 * Registered scrapers table is not the markup, it is the question "does this
 * module's declared brand actually exist in `watchBrands`, and if not, does an
 * override rescue it?" — which is exactly the question that was answered
 * silently and wrongly before, by a Run button that simply did not render.
 *
 * `extractorMatching.ts` owns the matching rule itself; this module is the
 * screen's derivation on top of it, plus the save payload.
 */

import {
  normalizeBrandOverrides,
  resolveDescriptorBrandId,
  type BrandOverrides,
  type ScraperDescriptor,
} from './extractorMatching';

/** A `watchBrands` document, as much of it as this screen renders. */
export interface BrandOption {
  id: string;
  name: string;
}

/** One entry of a descriptor's `supportedBrands`, resolved against the map. */
export interface ResolvedDescriptorBrand {
  /** What the module declared, normalized. */
  descriptorBrandId: string;
  /** The override applied, or null when the descriptor id is used as-is. */
  overrideId: string | null;
  /** The `watchBrands` doc id this entry stands for — override or literal. */
  brandId: string;
  /** Whether a brand document with that id was found. */
  exists: boolean;
  /** Display name of the resolved brand, when it exists. */
  name: string | null;
}

/** A row of the Registered scrapers table. */
export interface ScraperOverrideRow {
  descriptor: ScraperDescriptor;
  /** True for a multi-brand source declaring `'all'` — nothing to resolve. */
  isAllBrands: boolean;
  brands: ResolvedDescriptorBrand[];
  /** At least one declared brand resolves to no `watchBrands` document. */
  hasUnresolved: boolean;
  /** Brands this row can open the editor for — the resolved, existing ones. */
  openable: BrandOption[];
}

/** Declared brand ids, normalized and de-duplicated across the catalogue. */
export function descriptorBrandIds(
  descriptors: readonly ScraperDescriptor[] | null | undefined,
): string[] {
  const seen = new Set<string>();
  for (const d of descriptors ?? []) {
    if (!Array.isArray(d.supportedBrands)) continue;
    for (const b of d.supportedBrands) {
      if (typeof b !== 'string') continue;
      const id = b.trim().toLowerCase();
      if (id !== '') seen.add(id);
    }
  }
  return [...seen];
}

/**
 * Every `watchBrands` id the table needs to know the existence of.
 *
 * Both halves: the literal descriptor ids, and the override targets. An override
 * pointing at a brand that does not exist either is still a broken row, and
 * silently treating it as resolved would put the warning glyph on the wrong
 * rows — the ones the operator has already tried to fix.
 */
export function brandIdsToVerify(
  descriptors: readonly ScraperDescriptor[] | null | undefined,
  overrides?: BrandOverrides | null,
): string[] {
  const map = normalizeBrandOverrides(overrides);
  const ids = new Set(descriptorBrandIds(descriptors));
  for (const id of [...ids]) ids.add(resolveDescriptorBrandId(id, map));
  return [...ids];
}

/**
 * The table's rows.
 *
 * `knownBrands` is id → display name for the brands that were found; an id
 * absent from it is what raises the warning. The caller owns how that map is
 * built (Algolia for the catalogue, a targeted document read for the ids
 * Algolia doesn't carry — disabled brands are evicted from the index).
 */
export function buildOverrideRows(
  descriptors: readonly ScraperDescriptor[] | null | undefined,
  overrides: BrandOverrides | null | undefined,
  knownBrands: ReadonlyMap<string, string>,
): ScraperOverrideRow[] {
  const map = normalizeBrandOverrides(overrides);

  return (descriptors ?? []).map((descriptor) => {
    if (descriptor.supportedBrands === 'all' || !Array.isArray(descriptor.supportedBrands)) {
      return {
        descriptor,
        isAllBrands: descriptor.supportedBrands === 'all',
        brands: [],
        hasUnresolved: false,
        openable: [],
      };
    }

    const brands: ResolvedDescriptorBrand[] = [];
    for (const raw of descriptor.supportedBrands) {
      if (typeof raw !== 'string') continue;
      const descriptorBrandId = raw.trim().toLowerCase();
      if (descriptorBrandId === '') continue;

      const brandId = resolveDescriptorBrandId(descriptorBrandId, map);
      const name = knownBrands.get(brandId) ?? null;
      brands.push({
        descriptorBrandId,
        overrideId: brandId === descriptorBrandId ? null : brandId,
        brandId,
        exists: name !== null,
        name,
      });
    }

    return {
      descriptor,
      isAllBrands: false,
      brands,
      hasUnresolved: brands.some((b) => !b.exists),
      openable: brands
        .filter((b) => b.exists)
        .map((b) => ({ id: b.brandId, name: b.name ?? b.brandId })),
    };
  })
    // Alphabetical by display name. The service returns its catalogue in
    // registration order, which is meaningful to the extractor and to nobody
    // reading this table: an operator arrives knowing a brand's name and scans
    // for it. `localeCompare` rather than `<`, so accented names sort where a
    // reader expects them rather than after "Z".
    .sort((a, b) =>
      (a.descriptor.displayName ?? '').localeCompare(b.descriptor.displayName ?? ''),
    );
}

/**
 * Narrow the table to rows whose module name contains `query`.
 *
 * Display name only, case- and space-insensitive. Deliberately NOT the module
 * id: the ids are slugs that differ from the names precisely on the rows an
 * operator is least sure about, and matching them would return rows whose
 * visible name has nothing to do with what was typed.
 *
 * A blank query returns the rows unchanged (same array), so the no-search case
 * costs nothing and the caller needs no special path.
 */
export function filterScraperRows(
  rows: readonly ScraperOverrideRow[],
  query: string,
): ScraperOverrideRow[] {
  const q = query.trim().toLowerCase();
  if (q === '') return rows as ScraperOverrideRow[];
  return rows.filter((row) =>
    (row.descriptor.displayName ?? '').toLowerCase().includes(q),
  );
}

/**
 * The map to persist, out of the form's draft state.
 *
 * A blank box and a box holding the descriptor's own id both mean "no override"
 * and are dropped, so clearing a field removes the entry rather than storing a
 * value that does nothing. That matters because the stored map is also what an
 * operator reads to answer "which brands have been reconciled?" — a self-mapping
 * left in it is an answer of "this one" to a question it isn't the answer to.
 */
export function overridesForSave(draft: Record<string, string>): Record<string, string> {
  const normalized = normalizeBrandOverrides(draft);
  const out: Record<string, string> = {};
  for (const [from, to] of Object.entries(normalized)) {
    if (from !== to) out[from] = to;
  }
  return out;
}

/**
 * An extractor result envelope, with its `brandId` reconciled to a brand doc.
 *
 * The extractor stamps the envelope with the slug its module declares, and the
 * bulk-import wizard hard-rejects a file whose `brandId` is not the brand being
 * imported into — correctly, because importing an Omega crawl into Tudor writes
 * hundreds of mis-branded refs that cannot be undone per-doc. But those are the
 * same two strings a brand override exists to reconcile, so on an overridden
 * brand a run would produce a file the wizard refuses to import into the brand
 * it was started for.
 *
 * Rewriting it here is the override map's own statement, applied at the one
 * point that would otherwise contradict it — and ONLY there: an envelope whose
 * brand does not resolve to this one is a real mismatch and is passed through
 * untouched, for the wizard's stop to catch.
 */
export function reconcileEnvelopeBrandId(
  result: unknown,
  brandId: string,
  overrides?: BrandOverrides | null,
): unknown {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return result;
  const envelope = result as { brandId?: unknown };
  if (typeof envelope.brandId !== 'string' || envelope.brandId === brandId) return result;
  if (resolveDescriptorBrandId(envelope.brandId, normalizeBrandOverrides(overrides)) !== brandId) {
    return result;
  }
  return { ...envelope, brandId };
}

/**
 * The form's draft state, out of what was loaded.
 *
 * Every descriptor brand gets a key — including the ones with no override — so
 * the table renders one editable box per declared brand and a cleared box is a
 * deletion rather than an absence the save would ignore.
 */
export function draftFromOverrides(
  descriptors: readonly ScraperDescriptor[] | null | undefined,
  overrides: BrandOverrides | null | undefined,
): Record<string, string> {
  const map = normalizeBrandOverrides(overrides);
  const draft: Record<string, string> = {};
  for (const id of descriptorBrandIds(descriptors)) draft[id] = map[id] ?? '';
  /* Overrides for descriptors the service no longer registers stay editable. */
  for (const [from, to] of Object.entries(map)) {
    if (!(from in draft)) draft[from] = to;
  }
  return draft;
}
